import { QUICK_ACTIONS, availableQuickActions } from "../../utils/candidateFunnel";
import { telegramHref } from "../../utils/candidateMapper";

/**
 * Personal quick actions of a candidate row (правка №4/№14, Чат 1). [Claude] 2026-11-03
 * Which buttons are shown and in which order is chosen by each user («Быстрые действия»).
 */
const ICONS = {
    interview: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
    test: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
    consider: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
    reject: "M6 18L18 6M6 6l12 12",
    offer: "M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z",
    comment: "M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z",
    hh: "M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14",
    phone: "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
    telegram: "M12 19l9 2-9-18-9 18 9-2zm0 0v-8",
    edit: "M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z",
    delete: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16",
};

const TONE = {
    interview: "border-sky-200 text-sky-700 hover:bg-sky-50",
    test: "border-indigo-200 text-indigo-700 hover:bg-indigo-50",
    consider: "border-amber-200 text-amber-800 hover:bg-amber-50",
    reject: "border-rose-200 text-rose-700 hover:bg-rose-50",
    offer: "border-emerald-200 text-emerald-700 hover:bg-emerald-50",
    delete: "border-rose-200 text-rose-600 hover:bg-rose-50",
};

export function ActionIcon({ name, className = "h-4 w-4" }) {
    return <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS[name] || ICONS.edit} />
    </svg>;
}

export default function CandidateQuickActionButtons({ candidate, keys, compact = false, archived = false, busy = false, onAction }) {
    const shown = availableQuickActions(keys, candidate, { archived });
    if (!shown.length) return null;
    const byKey = Object.fromEntries(QUICK_ACTIONS.map((a) => [a.key, a]));
    const base = `inline-flex items-center gap-1 rounded-lg border bg-white text-xs font-semibold transition-colors disabled:opacity-40 ${compact ? "h-7 w-7 justify-center" : "px-2.5 py-1.5"}`;
    return <div className={`flex ${compact ? "flex-nowrap gap-1" : "flex-wrap gap-1.5"}`} data-testid="candidate-quick-actions">
        {shown.map((key) => {
            const action = byKey[key];
            const cls = `${base} ${TONE[key] || "border-slate-200 text-slate-600 hover:bg-slate-50"}`;
            const content = <><ActionIcon name={key} className="h-3.5 w-3.5" />{compact ? null : <span>{action.short}</span>}</>;
            if (key === "hh") {
                return <a key={key} href={candidate.hh_resume_link} target="_blank" rel="noreferrer" title={action.label}
                    onClick={(e) => e.stopPropagation()} className={cls}>{content}</a>;
            }
            if (key === "phone") {
                return <a key={key} href={`tel:${String(candidate.phone_number).replace(/[^\d+]/g, "")}`} title={`${action.label}: ${candidate.phone_number}`}
                    onClick={(e) => e.stopPropagation()} className={cls}>{content}</a>;
            }
            if (key === "telegram") {
                const href = telegramHref(candidate.telegram_username);
                return href ? <a key={key} href={href} target="_blank" rel="noreferrer" title={action.label}
                    onClick={(e) => e.stopPropagation()} className={cls}>{content}</a> : null;
            }
            return <button key={key} type="button" title={action.label} aria-label={action.label} disabled={busy}
                data-action={key} onClick={(e) => { e.stopPropagation(); onAction(key, candidate); }} className={cls}>
                {content}
            </button>;
        })}
    </div>;
}
